// src/hooks/useTickers.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTickersByUser,
  createTicker,
  updateTicker,
  deleteTicker,
} from '../api/tickers.ts'
import type { Ticker } from '../types.ts'

export function useTickers(userId: number) {
  return useQuery<Ticker[]>({
    queryKey: ['tickers', userId],
    queryFn: () => getTickersByUser(userId),
    select: (data) => [...data].sort((a, b) => a.ticker.localeCompare(b.ticker)),
  })
}

export function useCreateTicker(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<Ticker, 'id' | 'created_at'>) => createTicker(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickers', userId] }),
  })
}

export function useUpdateTicker(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Ticker, 'id' | 'created_at'>> }) =>
      updateTicker(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickers', userId] }),
  })
}

export function useDeleteTicker(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteTicker(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickers', userId] }),
  })
}
